<template>
  <section class="container-full-5">
    <div class="d-flex align-items-center gap-4 mb-6">
      <button class="btn white2" @click="goBack">
        <img src="/public/icons/icon-back.svg" class="btn-icon" alt="" />
        <span>Retour</span>
      </button>
      <h2 class="secondary-font gap-4 mt-2 mb-0">Membres - Édition du membre</h2>
    </div>

    <div class="clear-both h-0"></div>

    <div v-if="pageLoading" class="flex-center py-10">
      <div class="loader dark"></div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger mb-4">
      {{ errorMessage }}
    </div>

    <div v-if="successMessage" class="alert alert-success mb-4">
      {{ successMessage }}
    </div>

    <form v-if="!pageLoading" class="form widget s20 py-8 pb-10">
      <div class="container-full-1">
        <h4 class="font-semibold mb-5">Informations personnelles <span class="text-sm text-grey-500"></span></h4>

        <div class="form-field-floating reactive my-4">
          <input type="text" class="form-input" v-model="formData.nom" name="nom" id="nom" placeholder="" />
          <label for="nom">Nom <b class="color-danger">*</b></label>
        </div>
        <span v-if="errors.nom" class="text-red">{{ errors.nom }}</span>

        <div class="form-field-floating reactive my-4">
          <input type="text" class="form-input" v-model="formData.prenom" name="prenom" id="prenom" placeholder="" />
          <label for="prenom">Prénom <b class="color-danger">*</b></label>
        </div>
        <span v-if="errors.prenom" class="text-red">{{ errors.prenom }}</span>

        <div class="form-field-floating reactive my-4">
          <input type="email" class="form-input" v-model="formData.email" name="email" id="email" placeholder="" />
          <label for="email">Email <b class="color-danger">*</b></label>
        </div>
        <span v-if="errors.email" class="text-red">{{ errors.email }}</span>

        <div class="form-field-floating reactive my-4">
          <input type="text" class="form-input" v-model="formData.telephone" name="telephone" id="telephone" placeholder="" />
          <label for="telephone">Téléphone <b class="color-danger">*</b></label>
        </div>
        <span v-if="errors.telephone" class="text-red">{{ errors.telephone }}</span>

        <div class="form-field-floating reactive my-4">
          <input type="text" class="form-input" v-model="formData.fonction" name="fonction" id="fonction" placeholder="" />
          <label for="fonction">Fonction</label>
        </div>

        <div class="form-field-floating reactive my-4">
          <select class="form-input-select" v-model="formData.collectivite_id" name="collectivite_id" id="collectivite_id">
            <option :value="null">Sélectionnez une collectivité</option>
            <option :value="collectivite.id" v-for="collectivite in startupInfos.collectivites" :key="collectivite.id">
              {{ collectivite.nom }}
            </option>
          </select>
          <label for="collectivite_id">Collectivité <b class="color-danger">*</b></label>
        </div>
        <span v-if="errors.collectivite_id" class="text-red">{{ errors.collectivite_id }}</span>

        <div class="custom-upload-size margin-auto flex my-8">
          <label :for="'photo-upload'" class="custom-upload my-5" :class="{ active: formData.photo }">
            <div class="custom-upload-st2">
              <input
                :id="'photo-upload'"
                class="display-none"
                type="file"
                @change="handlePhotoUpload"
                accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
              />
              <div class="s5 custom-upload-img" :style="photoPreviewStyle">
                <img v-if="formData.photo_preview && typeof formData.photo_preview === 'string'" :src="formData.photo_preview" width="100%" style="object-fit: cover;" />
                <img v-else src="/public/icons/icon-profil.svg" width="45%" class="icon-white" />
              </div>
              <div class="s10">
                <span class="custom-upload-text uppercase">Photo de profil</span>
                <br />
                <span class="custom-upload-text-sm">
                  {{ formData.photo?.name || "Charger l'image" }}
                </span>
              </div>
              <div class="s5 right">
                <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
              </div>
            </div>
          </label>
        </div>

        <div class="clear-both h-30"></div>
        <p class="text-xs text-grey-500 mb-5"><b class="color-danger text-xs">*</b> Les champs marqués d'un astérisque sont obligatoires</p>
        <div class="flex gap-2">
          <button type="button" class="btn lg w-100" :disabled="loading" @click="onSubmit">
            <img src="/public/icons/icon-enregistrer.svg" class="btn-icon" alt="" />
            <span v-if="!loading">Enregistrer</span>
            <span v-else>Enregistrement...</span>
          </button>
        </div>
      </div>
    </form>
  </section>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import membersService from '@/services/membersService'
import { showAlert } from '@/utils/swal'

const route = useRoute()
const router = useRouter()

const pageLoading = ref(false)
const loading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const startupInfos = reactive({
  collectivites: [] as any[]
})

const formData = reactive({
  id: null as number | null,
  nom: '',
  prenom: '',
  email: '',
  telephone: '',
  fonction: '',
  collectivite_id: null as number | null,
  photo: null as { file: File; name: string } | null,
  photo_preview: null as string | null
})

const errors = reactive({
  nom: '',
  prenom: '',
  email: '',
  telephone: '',
  collectivite_id: ''
})

const photoPreviewStyle = computed(() => {
  if (formData.photo_preview && typeof formData.photo_preview === 'string') {
    return { background: `url('${formData.photo_preview}')`, backgroundSize: 'cover' }
  }
  return { background: "url('/public/icons/empty.png')", backgroundSize: 'cover' }
})

onMounted(async () => {
  const memberId = route.params.id
  if (memberId) {
    await loadStartupInfos()
    await loadMember(Number(memberId))
  } else {
    router.push({ name: 'collectivites-membres' })
  }
})

async function loadStartupInfos() {
  try {
    const data = await membersService.getStartupInfos()
    startupInfos.collectivites = data.collectivites || []
  } catch (error: any) {
    console.error('Erreur lors du chargement des données de démarrage:', error)
  }
}

async function loadMember(id: number) {
  try {
    pageLoading.value = true
    errorMessage.value = ''
    // Utiliser la nouvelle route frontoffice
    const data = await membersService.getMemberFormDatas(id)
    
    formData.id = data.user.id
    formData.nom = data.user.nom || ''
    formData.prenom = data.user.prenom || ''
    formData.email = data.user.email || ''
    formData.telephone = data.user.telephone || ''
    formData.fonction = data.user.fonction || ''
    formData.collectivite_id = data.collectivite_id || null
    
    if (data.user.photo) {
      formData.photo_preview = data.user.photo_url
    } else {
      formData.photo_preview = '/public/assets/users/300w/user2.jpg'
    }
  } catch (error: any) {
    errorMessage.value = error.message || 'Erreur lors du chargement du membre'
    setTimeout(() => {
      router.push({ name: 'collectivites-membres' })
    }, 2000)
  } finally {
    pageLoading.value = false
  }
}

function handlePhotoUpload(event: Event) {
  const input = event.target as HTMLInputElement
  if (!input.files || input.files.length === 0) {
    return
  }

  const file = input.files[0]
  const reader = new FileReader()
  reader.onload = e => {
    formData.photo = {
      file,
      name: file.name
    }
    formData.photo_preview = e.target?.result as string ?? null
  }
  reader.readAsDataURL(file)
}

function validateEmail(emailValue: string): boolean {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return regex.test(emailValue)
}

function validatePhone(phoneValue: string): boolean {
  const regex = /^\+?[0-9]{8,15}$/
  return regex.test(phoneValue)
}

function validate(): boolean {
  let isValid = true
  Object.keys(errors).forEach(key => {
    errors[key as keyof typeof errors] = ''
  })

  if (!formData.nom) {
    errors.nom = 'Le nom est requis'
    isValid = false
  }

  if (!formData.prenom) {
    errors.prenom = 'Le prénom est requis'
    isValid = false
  }

  if (!formData.email) {
    errors.email = "L'email est requis"
    isValid = false
  } else if (!validateEmail(formData.email)) {
    errors.email = "L'adresse email est invalide"
    isValid = false
  }

  if (!formData.telephone) {
    errors.telephone = 'Le téléphone est requis'
    isValid = false
  } else if (!validatePhone(formData.telephone)) {
    errors.telephone = 'Le téléphone est invalide'
    isValid = false
  }

  if (!formData.collectivite_id) {
    errors.collectivite_id = 'La collectivité est requise'
    isValid = false
  }

  return isValid
}

async function onSubmit() {
  if (!validate()) {
    return
  }

  loading.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const formToUpload = new FormData()
    if (formData.id != null) {
      formToUpload.append('id', `${formData.id}`)
    }
    formToUpload.append('nom', formData.nom)
    formToUpload.append('prenom', formData.prenom)
    formToUpload.append('email', formData.email)
    formToUpload.append('telephone', formData.telephone)
    formToUpload.append('fonction', formData.fonction || '')
    formToUpload.append('collectivite_id', `${formData.collectivite_id}`)

    if (formData.photo?.file) {
      formToUpload.append('photo', formData.photo.file, formData.photo.name)
    }

    // Utiliser la nouvelle route frontoffice pour la mise à jour
    await membersService.updateMemberFrontoffice(formData.id!, formToUpload)
    successMessage.value = 'Membre modifié avec succès.'
    await showAlert.success('Membre modifié avec succès.', 'Modification réussie')
    
    // Rediriger vers la liste après un court délai
    setTimeout(() => {
      router.push({ name: 'collectivites-membres' })
    }, 1500)
  } catch (error: any) {
    errorMessage.value = error.message || 'Erreur lors de la modification du membre.'
    await showAlert.error(errorMessage.value, 'Erreur de modification')
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.push({ name: 'collectivites-membres' })
}
</script>

<style scoped>
.custom-upload-size {
  max-width: 500px;
}
</style>

